import { computed, defineComponent, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { Popover } from 'bootstrap';
import { useDateFormat } from '@/composables/dateFormat';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { EnjoinAction, EnjoinGetter } from '@/store/enjoin/enjoin.module';
import { Enjoin } from '@/store/enjoin/enjoin.model';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { Province } from '@/store/province/province.model';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'EnjoinTableComponent',
  components: {
    LoadingComponent,
  },
  props: {
    fiscalYear: {
      type: Object as PropType<FiscalYear>
    },
    province: {
      type: Object as PropType<Province>
    }
  },
  setup: (props) => {
    const dateFormat = useDateFormat();
    const store = useStore();
    const router = useRouter();
    const enjoins = computed<Enjoin[]>(() => store.getters[EnjoinGetter.ALL]);
    const isLoading = computed(() => store.getters[EnjoinGetter.STATUS] === 'init' || store.getters[EnjoinGetter.STATUS] === 'loading');
    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();
    const popoverList = ref<Popover[]>([]);

    onMounted(() => {
      if (props.fiscalYear && props.province) {
        updateDataTable(props.fiscalYear.id, props.province.id);
      }
    });

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    watch(() => ([props.fiscalYear, props.province]), ([fiscalYear, province]) => {
      if (fiscalYear && province) {
        updateDataTable(fiscalYear.id, province.id);
      }
    });

    const updateDataTable = (fiscalYearId: number, provinceId: number) => {
      Promise.resolve(dtInstance.value?.destroy())
        .then(() => {
          store.dispatch(EnjoinAction.FETCH_BY_PROVINCE_ID, {
            fiscalYearId: fiscalYearId,
            provinceId: provinceId
          })
          .then(() => initialPopover())
          .then(() => initialDataTable());
        });
    };

    const initialPopover = () => {
      const popoverTriggerList = computed<HTMLElement[]>(() => [].slice.call(document.querySelectorAll('[data-bs-toggle="popover-enjoin"]')) as HTMLElement[]);
      popoverList.value.map(element => element.hide());
      popoverList.value = popoverTriggerList.value?.map(element => new Popover(element, { trigger: 'hover', placement: 'right' }));
    };

    const initialDataTable = () => {
      const dt = datatable.value as HTMLTableElement;
      if (!$.fn.dataTable.isDataTable(dt)) {
        dtInstance.value = $(dt).DataTable({
          language: {
            emptyTable: 'ไม่พบข้อมูล',
            info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
            infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
            infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
            lengthMenu: 'แสดงข้อมูล _MENU_',
            paginate: {
              next: 'ถัดไป',
              previous: 'ก่อนหน้า',
              first: 'First',
              last: 'Last'
            },
            search: 'ค้นหา:',
            zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
          }
        });
      }
    };

    const selectedClick = (enjoinId: number) => {
      Promise.resolve(popoverList.value.map(element => element.hide()))
        .then(() => router.push(`/enjoin/${enjoinId}`));
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'short');

    return {
      datatable,
      enjoins,
      isLoading,
      selectedClick,
      thaiDate,
    };
  }
});
